import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatCard } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { formatDate } from "@/lib/dateFormat";
import { fmt, sortSources } from "@/lib/finance";
import DriveLinkCell from "@/components/DriveLinkCell";
import DriveLinkPreviewModal from "@/components/DriveLinkPreviewModal";
import useBulkSelection from "@/hooks/useBulkSelection";
import useInlineBulkEdit from "@/hooks/useInlineBulkEdit";
import { EditableText, EditableSelect, EditableDate } from "@/components/EditableCell";
import BulkSaveBar from "@/components/BulkSaveBar";
import { matchesMonth } from "@/lib/finance";

// Phase 94 — Withdrawal rows are fully inline-editable + bulk-saved here.
// "Spent" rows mirror Expenses (Pay mode = Cash) 1:1 — edit those from the
// Entries Ledger so the two views never drift out of sync.
export default function PettyCashLedger({ month = "all" }) {
  const [summary, setSummary] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [sources, setSources] = useState([]);
  const [sourcesError, setSourcesError] = useState(null);
  const empty = { date: new Date().toISOString().slice(0, 10), amount: "", source_id: "", notes: "" };
  const [draft, setDraft] = useState(empty);
  const [docPreview, setDocPreview] = useState(null);

  const load = () => {
    api.get("/finance/petty-cash/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/finance/petty-cash/withdrawals").then(({ data }) => setWithdrawals(data || [])).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/finance/sources").then(({ data }) => { setSources(data || []); setSourcesError(null); })
      .catch((e) => { setSources([]); setSourcesError(e?.response?.status === 403 ? "denied" : "error"); });
  }, []);

  const addWithdrawal = async () => {
    if (!draft.amount || Number(draft.amount) <= 0) { toast.error("Amount must be greater than 0"); return; }
    try {
      await api.post("/finance/petty-cash/withdrawals", { ...draft, amount: Number(draft.amount) });
      setDraft(empty);
      toast.success("Withdrawal logged");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeWithdrawal = async (id) => {
    if (!window.confirm("Delete this withdrawal?")) return;
    try { await api.delete(`/finance/petty-cash/withdrawals/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const monthFilteredWithdrawals = useMemo(() => withdrawals.filter((r) => matchesMonth(r, month)), [withdrawals, month]);
  const { rows: editWithdrawals, updateCell, isDirty, dirtyCount, discardAll, bulkSave, saving } = useInlineBulkEdit({
    sourceRows: monthFilteredWithdrawals,
    endpoint: "/finance/petty-cash/withdrawals/bulk",
    numericFields: ["amount"],
    onSaved: load,
  });

  const spendRows = useMemo(() => (summary?.ledger || []).filter((r) => r.type === "spent" && matchesMonth(r, month)), [summary, month]);
  const merged = [...editWithdrawals.map((w) => ({ ...w, type: "withdrawal" })), ...spendRows].sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  const bulk = useBulkSelection({ collection: "petty_cash_withdrawals", ids: editWithdrawals.map((r) => r.id), onDone: load });

  if (!summary) return <div className="text-xs text-slate-400 p-6 text-center">Loading…</div>;

  return (
    <div>
      <BulkSaveBar count={dirtyCount} saving={saving} onSave={bulkSave} onDiscard={discardAll} testIdPrefix="petty-cash" />

      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Withdrawn (bank → cash)" value={fmt(summary.total_withdrawn)} />
        <StatCard label="Spent (Pay mode = Cash)" value={fmt(summary.total_spent)} accent="text-orange-700" />
        <StatCard label="Balance in hand (all-time)" value={fmt(summary.balance_in_hand)} accent={summary.balance_in_hand < 0 ? "text-red-700" : "text-green-700"} />
      </div>

      <div className="card-flat p-4 mb-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Log a bank withdrawal</div>
        <div className="grid grid-cols-4 gap-2 items-end">
          <div>
            <Label className="label-overline">Date</Label>
            <Input type="date" className="rounded-sm mt-1 h-8 text-xs" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} data-testid="petty-cash-withdrawal-date" />
          </div>
          <div>
            <Label className="label-overline">Amount ₹</Label>
            <Input type="number" className="rounded-sm mt-1 h-8 text-xs" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} data-testid="petty-cash-withdrawal-amount" />
          </div>
          <div>
            <Label className="label-overline">Source</Label>
            <Select value={draft.source_id} onValueChange={(v) => setDraft({ ...draft, source_id: v })}>
              <SelectTrigger className="rounded-sm mt-1 h-8 text-xs" data-testid="petty-cash-withdrawal-source"><SelectValue placeholder="Bank / card" /></SelectTrigger>
              <SelectContent>{sortSources(sources).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            {sourcesError && <p className="text-[10px] text-red-600 mt-0.5" data-testid="petty-cash-sources-error">{sourcesError === "denied" ? "Access denied loading sources." : "Couldn't load sources."}</p>}
          </div>
          <Button onClick={addWithdrawal} className="rounded-sm h-8 text-xs bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="petty-cash-withdrawal-save">
            <Plus size={12} className="mr-1" /> Log withdrawal
          </Button>
        </div>
        <Input className="rounded-sm mt-2 h-8 text-xs" placeholder="Notes (optional)" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} data-testid="petty-cash-withdrawal-notes" />
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm" data-testid="petty-cash-ledger-table">
          <thead className="bg-slate-50">
            <tr>
              {bulk.canBulk && (<th className="w-8 px-2 py-2"><bulk.HeaderCheckbox /></th>)}
              {["Date", "Type", "Narration", "Source", "Project", "Amount ₹", "Drive Link", "Actions"].map((h) => (
                <th key={h} className="label-overline px-2 py-2 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {merged.map((r) => {
              const isWithdrawal = r.type === "withdrawal";
              const dirty = isWithdrawal && isDirty(r.id);
              return (
                <tr key={`${r.type}-${r.id}`} className={`hover:bg-slate-50 ${dirty ? "!bg-orange-50 border-l-2 border-[#F54800]" : ""} ${isWithdrawal && bulk.isSelected(r.id) ? "!bg-orange-100" : ""}`} data-testid={`petty-cash-row-${r.id}`}>
                  {bulk.canBulk && (
                    <td className="px-2 py-2">{isWithdrawal ? <bulk.RowCheckbox id={r.id} /> : null}</td>
                  )}
                  <td className="px-2 py-2">
                    {isWithdrawal ? (
                      <EditableDate value={r.date} onChange={(v) => updateCell(r.id, "date", v)} testId={`petty-cell-date-${r.id}`} />
                    ) : (
                      <span className="text-xs text-slate-600 whitespace-nowrap">{formatDate(r.date)}</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-xs">
                    <span className={isWithdrawal ? "text-green-700" : "text-orange-700"}>{isWithdrawal ? "Withdrawal" : "Spent"}</span>
                  </td>
                  <td className="px-2 py-2 text-xs max-w-[220px] truncate" title={r.narration}>
                    {isWithdrawal ? (
                      <EditableText className="w-40" value={r.notes} onChange={(v) => updateCell(r.id, "notes", v)} placeholder="Notes" testId={`petty-cell-notes-${r.id}`} />
                    ) : r.narration}
                  </td>
                  <td className="px-2 py-2 w-32">
                    {isWithdrawal ? (
                      <EditableSelect value={r.source_id} onChange={(v) => updateCell(r.id, "source_id", v)} options={sortSources(sources).map((s) => ({ value: s.id, label: s.name }))} testId={`petty-cell-source-${r.id}`} />
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="px-2 py-2 text-xs">{r.project_name || "—"}</td>
                  <td className="px-2 py-2">
                    {isWithdrawal ? (
                      <EditableText type="number" align="right" className="w-24" value={r.amount} onChange={(v) => updateCell(r.id, "amount", v)} testId={`petty-cell-amount-${r.id}`} />
                    ) : (
                      <div className="text-xs font-mono text-right whitespace-nowrap text-orange-700">−{fmt(r.amount)}</div>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <DriveLinkCell
                      value={r.drive_link}
                      onChange={isWithdrawal ? (v) => updateCell(r.id, "drive_link", v) : undefined}
                      onPreview={(url) => setDocPreview({ url, title: isWithdrawal ? "Withdrawal slip" : `Receipt — ${r.narration || r.id}` })}
                      readOnly={!isWithdrawal}
                      testId={`petty-cell-drive-link-${r.id}`}
                    />
                  </td>
                  <td className="px-2 py-2 text-right">
                    {isWithdrawal && (
                      <button onClick={() => removeWithdrawal(r.id)} className="p-1.5 text-slate-400 hover:text-red-600" title="Delete withdrawal" data-testid={`petty-cash-delete-${r.id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {merged.length === 0 && (
              <tr><td colSpan={bulk.canBulk ? 8 : 7} className="px-3 py-6 text-center text-xs text-slate-400">No petty-cash activity this month.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <bulk.ActionBar />
      <bulk.ConfirmDialog />
      <DriveLinkPreviewModal open={!!docPreview} url={docPreview?.url} title={docPreview?.title} onClose={() => setDocPreview(null)} />
    </div>
  );
}
